import React from 'react';
import ConfirmDialog from './ConfirmDialog';

/**
 * THE shared core of a confirmation that demands a reason — ONE definition of
 * "required" for every dialog in front of an approval bypass.
 *
 * Extracted 2026-09-14 when approve-on-behalf became the second such dialog
 * (owner: *"add the text input for 'Next step → Approve on behalf of Test
 * Buyer…' as well as this is still an override"*). `SkipApprovalsDialog` and
 * `ApproveOnBehalfDialog` were about to be the same `ConfirmDialog` wiring
 * twice — a controlled textarea plus a trimmed-emptiness gate — and the gate
 * is exactly the part that must never drift: one copy testing truthiness
 * instead of `.trim()` offers to send the blank reason the server refuses.
 * Each wrapper keeps its OWN copy deck, variant, labels and focus; this file
 * owns the reason and nothing else.
 *
 * 🔑 **`confirmDisabled` UNTIL THE TRIMMED REASON IS NON-EMPTY.** It is OR'd
 * into `ConfirmDialog`'s own gate, so it can only make Confirm harder to press.
 * The SERVER is the real gate either way.
 *
 * 🔑 **`onConfirm(trimmedReason)`** — the trimmed string is handed over so a
 * caller never has to remember to trim. A caller that ignores the argument and
 * reads its own state (as `SkipApprovalsDialog`'s parent does) is unaffected.
 *
 * 🚨 **THE PARENT OWNS `reason`** — `ConfirmDialog`'s textarea is CONTROLLED,
 * so the value must live above it; holding it here would lose the text on any
 * parent re-render that remounted the dialog. See `SkipApprovalsDialog.js`.
 *
 * `reasonMaxLength` is optional and becomes the textarea's `maxLength`;
 * omitted, no attribute is rendered (so `SkipApprovalsDialog` is unchanged).
 *
 * 🔀 **`reasonRequired` (OPTIONAL, default `true`) — ADDED 2026-10-03 WITH THE
 * CANCEL/REJECT DIALOGS THAT REPLACED `window.prompt`.** Pass `false` and the
 * textarea, its label, its `maxLength` and the single trim point are all
 * exactly as above, with ONLY the Confirm gate dropped — which is the whole and
 * only difference between "a reason is demanded" and "a reason is invited".
 * `onConfirm` is then handed `''` for an untouched box, and a caller that wants
 * to send nothing tests for that.
 * 🔑 **WHY A PROP RATHER THAN A SIBLING FILE, which is the decision to carry:**
 * the optional case needs the identical controlled-textarea wiring — four
 * `ConfirmDialog` props plus the trim — and a second file expressing it would be
 * precisely the copy the Code Unification Principle forbids, with the
 * trimmed-emptiness rule then living in two places. This file exists *because*
 * that gate must have one definition; splitting it to keep the filename literal
 * would trade the thing it guards for the name on the tin.
 * ⚠️ **SO THE FILENAME IS NOW WIDER THAN ITS CONTENTS, AND THAT IS DECLARED,
 * NOT OVERLOOKED.** It is THE reason-capturing confirmation, required or not.
 * Renaming it to `ReasonDialog.js` is a tidy-up worth doing — it touches
 * `SkipApprovalsDialog`, `ApproveOnBehalfDialog`, `OrderReasonDialog`, the root
 * `CLAUDE.md` index row and their tests — and is an OWNER CALL, deliberately
 * not smuggled into a behaviour change.
 * 🚫 Both existing callers pass nothing, so `SkipApprovalsDialog` and
 * `ApproveOnBehalfDialog` are BYTE-IDENTICAL: the default keeps the approval
 * bypasses demanding a reason, which is the ruling this file was built for.
 */
export default function RequiredReasonDialog({
  reason,
  onReasonChange,
  reasonLabel,
  reasonMaxLength,
  reasonRequired = true,
  onConfirm,
  ...dialogProps
}) {
  const trimmed = String(reason || '').trim();
  return (
    <ConfirmDialog
      {...dialogProps}
      promptLabel={reasonLabel}
      promptValue={reason || ''}
      onPromptChange={onReasonChange}
      promptMaxLength={reasonMaxLength}
      // 🪤 `reasonRequired && !trimmed`, never a bare `!trimmed` behind a
      // ternary: `ConfirmDialog` OR's this into its own gate, so `false` here
      // can only ever mean "add no condition of my own".
      confirmDisabled={reasonRequired && !trimmed}
      onConfirm={() => onConfirm?.(trimmed)}
    />
  );
}
